import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Mail, Phone } from "lucide-react";
import { formatCents } from "@shared/money";
import { contactPolicy } from "@shared/contactPolicy";
import { normalizePhone } from "@shared/phone";
import { contactPatch } from "@shared/schemas/contacts";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { validateContact } from "@shared/validation/contacts";
import { evaluation, parseInput } from "@shared/validation/issues";
import { recordValidationQuery } from "@web/lib/validation";
import { fmtDate, fmtPhone, fullName, statusLabel, statusTone } from "@web/lib/format";
import { api, contactQuery, type ContactDetail } from "@web/lib/queries";
import { queryClient } from "@web/lib/session";
import { Avatar, BottomSheet, Button, Card, ColorDot, EmptyState, FormGrid, Inline, LeadCard, Row, RowGroup, Skeleton, Stack, StatusChip, Text, TextArea, TextField, Toggle, ValidationSummary, useToast } from "@web/ui";

export type ContactSheetProps = { contactId: string | null; onClose: () => void };

/** The Contact sheet (SPEC §6.1 item 4/8): details, consent flags, every booking for this person. */
export function ContactSheet({ contactId, onClose }: ContactSheetProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery({ ...contactQuery(contactId ?? ""), enabled: Boolean(contactId) });
  const validation = useQuery(recordValidationQuery("contact", contactId ?? ""));
  const c = q.data;
  const call = contactPolicy(c ?? null, "call"), email = contactPolicy(c ?? null, "email");
  const [editing, setEditing] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const close = () => { if (!editorBusy) { setEditing(false); onClose(); } };

  async function flag(key: "smsConsent" | "emailOptOut" | "dnc", on: boolean) {
    if (!contactId || !c) return;
    const k = contactQuery(contactId).queryKey;
    queryClient.setQueryData<ContactDetail>(k, { ...c, [key]: on ? 1 : 0 });
    try {
      await api.patchContact(contactId, { [key]: on });
    } catch (e) {
      queryClient.setQueryData(k, c);
      toast.error(errorMessage(e));
    }
  }

  return (
    <BottomSheet open={Boolean(contactId)} onClose={close} title={editing ? "Edit contact" : c ? fullName(c) || "Contact" : "Contact"}>
      {q.isError ? <EmptyState title="Unable to load contact" body={errorMessage(q.error)} action={<Button onClick={() => q.refetch()}>Try again</Button>} /> : !c ? (
        <Skeleton variant="cards" count={2} />
      ) : editing ? <ContactEditor key={c.id} contact={c} onDone={() => setEditing(false)} onBusyChange={setEditorBusy} /> : (
        <Stack gap={4}>
          <ValidationSummary result={validation.data} saved loading={validation.isFetching} unavailable={validation.isError} onRetry={() => void validation.refetch()} onIssue={() => setEditing(true)} />
          <Card>
            <Inline gap={3} align="start">
              <Avatar name={fullName(c) || "?"} />
              <Stack gap={1} className="flex-1">
                <Text variant="headline">{fullName(c) || "No name"}</Text>
                {(c.companyName || c.title) && (
                  <Text variant="callout" tone="secondary">
                    {[c.companyName, c.title].filter(Boolean).join(" · ")}
                  </Text>
                )}
                {c.phone && (
                  <Button variant="secondary" href={call.allowed ? `tel:${c.phone}` : undefined} disabled={!call.allowed} title={call.reason ?? undefined}>
                    <Inline gap={2}>
                      <Phone aria-hidden size={16} />
                      <Text as="span" variant="callout" tone="accent">
                        {fmtPhone(c.phone)}
                      </Text>
                    </Inline>
                  </Button>
                )}
                {c.email && (
                  <Button variant="secondary" href={email.allowed ? `mailto:${c.email}` : undefined} disabled={!email.allowed} title={email.reason ?? undefined}>
                    <Inline gap={2}>
                      <Mail aria-hidden size={16} />
                      <Text as="span" variant="callout" tone="accent">
                        {c.email}
                      </Text>
                    </Inline>
                  </Button>
                )}
                {[...new Set([c.phone && call.reason, c.email && email.reason].filter((reason): reason is string => Boolean(reason)))].map((reason) => <Text key={reason} as="p" variant="caption" tone="secondary">{reason}</Text>)}
                {c.address && (
                  <Text variant="callout" tone="secondary">
                    {c.address}
                  </Text>
                )}
              </Stack>
            </Inline>
          </Card>
          <Button variant="secondary" onClick={() => setEditing(true)}>Edit contact</Button>
          <Card>
            <Stack gap={1}>
              <Toggle label="SMS consent" description="Marketing texts need consent" checked={Boolean(c.smsConsent)} onChange={(v) => flag("smsConsent", v)} />
              <Toggle label="Email opt-out" description="No marketing email" checked={Boolean(c.emailOptOut)} onChange={(v) => flag("emailOptOut", v)} />
              <Toggle label="Do not contact" description="Blocks all non-transactional messages" checked={Boolean(c.dnc)} onChange={(v) => flag("dnc", v)} />
            </Stack>
          </Card>
          <Stack gap={2}>
            <Text variant="headline">Leads</Text>
            {c.leads.length === 0 ? <Card><EmptyState title="No leads yet" /></Card> : <RowGroup>{c.leads.map((lead) => <LeadCard key={lead.id} card={lead.card} status={lead.status} venueColor={lead.venueColor} detail={lead.role === "secondary" ? "Secondary contact" : undefined} onClick={() => { close(); navigate({ to: "/leads/$id", params: { id: lead.id } }); }} />)}</RowGroup>}
          </Stack>
          <Stack gap={2}>
            <Text variant="headline">Bookings</Text>
            {c.bookings.length === 0 ? (
              <Card>
                <EmptyState title="No bookings yet" />
              </Card>
            ) : (
              <RowGroup>
                {c.bookings.map((b) => (
                  <Row
                    key={b.id}
                    icon={<ColorDot color={b.venueColor} />}
                    title={`${fmtDate(b.eventDate, { weekday: true })}${b.eventName ? ` · ${b.eventName}` : ""}`}
                    subtitle={`${b.venueName ?? ""}${b.role === "secondary" ? " · secondary contact" : ""} · ${formatCents(b.total)}${b.balanceDue > 0 ? ` · due ${formatCents(b.balanceDue)}` : ""}`}
                    trailing={<StatusChip tone={statusTone(b.status)}>{statusLabel(b.status)}</StatusChip>}
                    onClick={() => {
                      close();
                      navigate({ to: "/bookings/$id", params: { id: b.id } });
                    }}
                  />
                ))}
              </RowGroup>
            )}
          </Stack>
        </Stack>
      )}
    </BottomSheet>
  );
}

function ContactEditor({ contact, onDone, onBusyChange }: { contact: ContactDetail; onDone: () => void; onBusyChange: (busy: boolean) => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ firstName: contact.firstName, lastName: contact.lastName, companyName: contact.companyName ?? "", title: contact.title ?? "", phone: contact.phone ?? "", email: contact.email ?? "", address: contact.address ?? "", notes: contact.notes ?? "" });
  const [baseline] = useState(form);
  const phoneChanged = normalizePhone(form.phone) !== normalizePhone(contact.phone);
  const toBody = (value: typeof form) => ({ ...value, email: value.email.trim() || null, phone: value.phone.trim() || null, companyName: value.companyName.trim() || null, title: value.title.trim() || null });
  const input = changedValues(toBody(form), toBody(baseline));
  const parsed = parseInput(contactPatch, input, { type: "contact", id: contact.id }, "contact.save");
  const local = validateContact({ ...contact, ...input }, { before: contact, changedFields: Object.keys(input), action: "contact.save" });
  const validation = useFormValidation(evaluation([...parsed.issues, ...local.issues], false));
  const { busy, field } = validation;
  const set = (key: keyof typeof form, value: string) => validation.edit(() => setForm((old) => ({ ...old, [key]: value })));
  async function save() {
    if (!parsed.success || !validation.begin("contact.save")) return;
    onBusyChange(true);
    try { await api.patchContact(contact.id, parsed.data); toast.success("Contact saved"); onDone(); }
    catch (e) { validation.reject(e); toast.error(errorMessage(e)); }
    finally { validation.finish(); onBusyChange(false); }
  }
  return <Stack gap={3}>
    <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
    <FormGrid>
      <TextField label="First name" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} autoFocus {...field("firstName")} />
      <TextField label="Last name" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} {...field("lastName")} />
      <TextField label="Company" value={form.companyName} onChange={(e) => set("companyName", e.target.value)} {...field("companyName")} />
      <TextField label="Job title" value={form.title} onChange={(e) => set("title", e.target.value)} {...field("title")} />
      <TextField label="Phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} {...field("phone")} />
      <TextField label="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} {...field("email")} />
    </FormGrid>
    {Boolean(contact.smsConsent) && phoneChanged && <Text variant="callout" tone="secondary">Changing this phone clears SMS consent. Save the number, then enable SMS consent only if this contact agreed to texts at the new number.</Text>}
    <TextArea label="Address" value={form.address} onChange={(e) => set("address", e.target.value)} {...field("address")} />
    <TextArea label="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} {...field("notes")} />
    <Inline gap={2}><Button variant="secondary" disabled={busy} onClick={onDone}>Cancel</Button><Button loading={busy} onClick={save}>Save contact</Button></Inline>
  </Stack>;
}
